'use client';

import { useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { globalErrorStore } from "../../stores/globalError.store.ts";

export default function GlobalErrorListener() {
  const { t } = useTranslation();
  const error = globalErrorStore((s) => s.error);
  const clearError = globalErrorStore((s) => s.clearError);

  useEffect(() => {
    if (!error) return;

    if (error.type === "snackBar") {
      toast.error(error.message);
      clearError();
    }
  }, [error, clearError]);

  // fullScreen errors (no response / 5xx / 401 / unexpected) used to be written to
  // the store and never rendered — a backend outage looked like a blank screen.
  // Surface them as a blocking overlay with a retry so failures are always visible.
  if (!error || error.type !== "fullScreen") return null;

  const isAuth = error.statusCode === 401;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-6"
      role="alertdialog"
      aria-modal="true"
      aria-label={t("errors.aria_error")}
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6 text-red-500"
            aria-hidden="true"
          >
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h2 className="text-base font-bold text-gray-900">
          {isAuth ? t("errors.session_expired") : t("errors.something_wrong")}
        </h2>
        <p className="mt-2 break-words text-sm text-gray-500">
          {isAuth
            ? t("errors.sign_in_again")
            : error.message || t("errors.network_error")}
        </p>
        <div className="mt-5 flex gap-2">
          <button
            type="button"
            onClick={clearError}
            className="flex-1 rounded-lg border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            {t("errors.dismiss")}
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex-1 rounded-lg bg-gray-900 py-2.5 text-sm font-semibold text-white hover:bg-black"
          >
            {t("errors.try_again")}
          </button>
        </div>
      </div>
    </div>
  );
}
